/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2021 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import { getQueryVar } from '@nebula/rover';
import { assoc, find, groupBy, head, map, pipe, prop, propEq, propOr } from 'ramda';
import { createSelector } from 'reselect';

import { selectOrgWorkspaceSettings, selectUserWorkspaceSettings } from './settings.selectors';
import { activeWorkspace } from '../../casing/data/prefabs';
import * as workspaceConfigs from '../../workspaces/config';

export const selectWorkspaceQuery = state => getQueryVar('workspace', state);

export const selectWorkspaceConfig = createSelector(
  selectWorkspaceQuery,
  configName => workspaceConfigs[configName] || {}
);

export const selectWorkspaces = createSelector(
  selectWorkspaceConfig,
  selectOrgWorkspaceSettings,
  selectUserWorkspaceSettings,
  (workspaceConfig, orgWorkspaces, userWorkspaces) => {
    const defaultWorkspaces = pipe(
      propOr([], 'workspaces'),
      map(assoc('type', 'default'))
    )(workspaceConfig);

    return [
      ...defaultWorkspaces,
      ...(workspaceConfig.allowOrgWorkspaces ? orgWorkspaces : []),
      ...(workspaceConfig.allowUserWorkspaces ? userWorkspaces : [])
    ];
  }
);

export const selectGroupedWorkspaces = createSelector(
  selectWorkspaces,
  groupBy(prop('type'))
);

export const findWorkspace = workspace => find(propEq('id', workspace));

export const getWorkspaceViewPlugins = prop('enabledViewPlugins');

export const getWorkspaceValidationPlugins = prop('validationPlugins');

export const selectActiveWorkspace = createSelector(
  selectWorkspaces,
  activeWorkspace.value,
  (workspaces, active) => findWorkspace(active)(workspaces) || head(workspaces)
);

export const selectActiveWorkspaceViewPlugins = createSelector(
  selectActiveWorkspace,
  getWorkspaceViewPlugins
);

export const selectActiveWorkspaceValidationPlugins = createSelector(
  selectActiveWorkspace,
  getWorkspaceValidationPlugins
);
